import { useContext, useState } from 'react';

import config from '@/config';
import ProfileDataContext from '@/context/ProfileDataContext';
import useCache from '@/hooks/useCache';
import useTranslations from '@/i18n/useTranslations';
import CreateArtist from '@/shared-components/forms/create-artist';
import ProfileContentLoader from '@/shared-components/loader/special-loaders/ProfileContentLoader';
import Text from '@/shared-components/text/components/Text';

import MetaDescripProfilePages from '../MetaDescripProfilePages';

import ArtworksOfArtist from './artworks-of-artist';
import VirtualList from './virtual-list';

import './index.css';

export default function ProfileArtists() {
  const { profileData, internal } = useContext(ProfileDataContext);
  const t = useTranslations('common.profile.pages.artists');
  const { loading, fetchData, setFetchData, loadMoreData } = useCache(
    `${profileData._id}_artists`,
    `${config.apis.api.url}/gallery/${profileData._id}/artists`,
    {
      injectToken: true,
      invalidateWhen: internal
        ? [
          "BUY_ARTWORK",
          "EDIT_ARTWORK",
          "DELETE_ARTWORK",
          "NEW_ARTIST",
          `${profileData._id}_REFRESH`,
        ]
        : [`${profileData._id}_REFRESH`],
    }
  );
  const [artistToEdit, setArtistToEdit] = useState(null);
  const [artistToOpen, setArtistToOpen] = useState(null);
  const publicArtistCount = fetchData?.totalDocs ?? fetchData?.data?.length ?? 0;

  function onEditGalleryArtist(newGalleryArist) {
    setFetchData({
      ...fetchData,
      data: fetchData.data.map((artist) => {
        if (artist._id === newGalleryArist._id) {
          return {
            ...newGalleryArist,
            artwork_count: artist.artwork_count,
            artworks: artist.artworks,
          };
        } else {
          return artist;
        }
      }),
    });
  }

  return (
    <>
      <MetaDescripProfilePages
        fetchData={profileData}
        subPage="artists"
        internal={internal}
        itemCount={publicArtistCount}
      />
      {loading ? (
        <ProfileContentLoader className="profile-artists__artists-spinner" />
      ) : fetchData.data.length === 0 ? (
        <Text className="user-profile__artists-empty-view font-size-small" paragraph>
          {internal
            ? t('empty.internal')
            : t('empty.external')}
        </Text>
      ) : (
        <VirtualList
          items={fetchData}
          profileData={profileData}
          internal={internal}
          onLoadMore={loadMoreData}
          openArtistPreview={setArtistToOpen}
          openEditArtist={setArtistToEdit}
        />
      )}
      <ArtworksOfArtist
        open={Boolean(artistToOpen)}
        close={() => setArtistToOpen(null)}
        artist={artistToOpen}
        profileData={profileData}
        internal={internal}
      />
      <CreateArtist
        open={Boolean(artistToEdit)}
        close={() => setArtistToEdit(null)}
        onCreate={onEditGalleryArtist}
        artistData={artistToEdit}
        editMode
      />
    </>
  );
}
